import React from 'react';

/**
 * FirstAidForm Component
 * 
 * A React functional component for capturing first aid incident details. 
 * This form allows users to input information about the type of injury, 
 * the body part affected, treatment provided, and whether further medical 
 * attention was required.
 * 
 * @component
 * 
 * @param {Object} props - The component props.
 * @param {Object} props.formData - The initial form data (not used in local state).
 * @param {Function} props.onInputChange - Callback function to handle input changes (not used in this component).
 * @param {Function} props.onFormSubmit - Callback function to handle form submission. 
 *                                         Receives the local state `firstAidData` as an argument.
 * @param {string} props.incidentId - The ID of the incident being reported.
 * 
 * @returns {JSX.Element} The rendered FirstAidForm component.
 * 
 * @example
 * <FirstAidForm 
 *   formData={initialData} 
 *   onInputChange={handleInputChange} 
 *   onFormSubmit={handleFormSubmit} 
 *   incidentId="12345"
 * />
 */
const FirstAidForm = (props) => {
  // Local state for this form's specific fields
  const [firstAidData, setFirstAidData] = React.useState({
    treatmentProvided: '',
    bodyPartAffected: '',
    injuryType: '',
    medicalAttentionRequired: 'No'
  });

  const [loading, setLoading] = React.useState(false);

  // Add submission lock
  const isFormSubmitting = React.useRef(false);

  // Handle local form changes
  const handleLocalChange = (e) => {
    const { name, value } = e.target;
    setFirstAidData({
      ...firstAidData,
      [name]: value
    });
  };

  // Submit the form data to parent
  const handleSubmit = (e) => {
    e.preventDefault();
    e.stopPropagation(); // Critical: stop event propagation
    
    // Guard against multiple submissions
    if (isFormSubmitting.current || props.loading) {
      console.log('Form already submitting, preventing duplicate');
      return;
    }
    
    // Set lock flag
    isFormSubmitting.current = true;
    
    // Call the parent's submit handler
    const result = props.onFormSubmit(firstAidData);
    
    // If it returns a promise, wait for it to complete
    if (result && typeof result.finally === 'function') {
      result.finally(() => {
        // Release lock after a timeout
        setTimeout(() => {
          isFormSubmitting.current = false;
        }, 1000);
      });
    } else {
      // If not a promise, release after a delay
      setTimeout(() => {
        isFormSubmitting.current = false;
      }, 1000);
    }
  };
 
  return (
    <form onSubmit={handleSubmit}>
      <h3 className="text-lg font-medium mb-4">First Aid Incident Details</h3>
      
      <div className="mb-4">
        <label className="block text-gray-700 mb-1">Type of Injury</label>
        <select
          name="injuryType"
          value={firstAidData.injuryType}
          onChange={handleLocalChange}
          className="w-full border rounded-md px-3 py-2"
          required
        >
          <option value="">Select injury type...</option>
          <option value="Cut/Laceration">Cut/Laceration</option>
          <option value="Burn">Burn</option>
          <option value="Sprain/Strain">Sprain/Strain</option>
          <option value="Bruise">Bruise</option>
          <option value="Fracture">Fracture</option>
          <option value="Foreign Object">Foreign Object</option>
          <option value="Other">Other</option>
        </select>
      </div>
      
      <div className="mb-4">
        <label className="block text-gray-700 mb-1">Body Part Affected</label>
        <input
          type="text"
          name="bodyPartAffected"
          value={firstAidData.bodyPartAffected}
          onChange={handleLocalChange}
          className="w-full border rounded-md px-3 py-2"
          required
        />
      </div>
      
      <div className="mb-4">
        <label className="block text-gray-700 mb-1">Treatment Provided</label>
        <textarea
          name="treatmentProvided"
          value={firstAidData.treatmentProvided}
          onChange={handleLocalChange}
          className="w-full border rounded-md px-3 py-2 h-24"
          required
          placeholder="Describe first aid administered"
        ></textarea>
      </div>
      
      <div className="mb-4">
        <label className="block text-gray-700 mb-1">Was medical attention beyond first aid required?</label>
        <div className="flex gap-4">
          <label className="inline-flex items-center">
            <input
              type="radio"
              name="medicalAttentionRequired"
              value="Yes"
              checked={firstAidData.medicalAttentionRequired === 'Yes'}
              onChange={handleLocalChange}
              className="mr-2"
            />
            Yes
          </label>
          <label className="inline-flex items-center">
            <input
              type="radio"
              name="medicalAttentionRequired"
              value="No"
              checked={firstAidData.medicalAttentionRequired === 'No'}
              onChange={handleLocalChange}
              className="mr-2"
            />
            No
          </label>
        </div>
      </div>
      
      <div className="flex justify-end">
        <button 
          type="submit" 
          disabled={props.loading}
          className={props.loading ? 'submit-button disabled' : 'submit-button'}
          onClick={(e) => {
            if (props.loading) {
              e.preventDefault();
              e.stopPropagation();
              return false;
            }
          }}
        >
          {props.loading ? 'Submitting...' : 'Submit'}
        </button>
      </div>
    </form>
  );
};

export default FirstAidForm;